import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Users, Scissors, Store, Package, GraduationCap } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoenixMark } from "@/components/Logo";

const ROLES = [
  { v: "client", label: "Client", icon: Users, d: "Find & follow barbers" },
  { v: "barber", label: "Barber", icon: Scissors, d: "Get certified & seen" },
  { v: "shop", label: "Shop", icon: Store, d: "Grow your team" },
  { v: "vendor", label: "Vendor", icon: Package, d: "Reach the audience" },
  { v: "educator", label: "Educator", icon: GraduationCap, d: "Teach & earn" },
];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("barber");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register({ name, email, password, role });
      toast.success("Account created. Welcome to Barbistry.");
      navigate("/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] px-5 py-12">
      <div className="w-full max-w-lg">
        <Link to="/" className="inline-flex items-center gap-2 mb-8"><PhoenixMark className="h-10 w-10" /><span className="font-display text-xl font-bold">Barb<span className="gold-text">istry</span></span></Link>
        <h1 className="font-display text-3xl font-bold">Join Barbistry</h1>
        <p className="mt-2 text-sm text-neutral-400">Pick your path and start building status.</p>

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-2">
          {ROLES.map((r) => (
            <button key={r.v} type="button" onClick={() => setRole(r.v)} data-testid={`role-${r.v}`}
              className={`p-3 border text-center transition-colors ${role === r.v ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 hover:border-white/30"}`}>
              <r.icon size={20} className={`mx-auto ${role === r.v ? "text-[#D4AF37]" : "text-neutral-400"}`} />
              <div className={`mt-1.5 text-xs font-medium ${role === r.v ? "text-[#D4AF37]" : "text-neutral-300"}`}>{r.label}</div>
            </button>
          ))}
        </div>

        <Button onClick={googleLogin} data-testid="google-register-btn" variant="outline" className="mt-6 w-full rounded-none h-11 bg-white text-black hover:bg-neutral-200 border-none font-medium">
          Continue with Google
        </Button>
        <div className="flex items-center gap-3 my-5 text-xs text-neutral-600"><div className="flex-1 h-px bg-white/10" />OR<div className="flex-1 h-px bg-white/10" /></div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-neutral-300">Full name</Label>
            <Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" className="mt-1.5 bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" />
          </div>
          <div>
            <Label className="text-neutral-300">Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email" className="mt-1.5 bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" />
          </div>
          <div>
            <Label className="text-neutral-300">Password</Label>
            <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" className="mt-1.5 bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" />
          </div>
          {err && <p className="text-sm text-red-400" data-testid="register-error">{err}</p>}
          <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full rounded-none h-11 bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold">{loading ? "Creating…" : "Create account"}</Button>
        </form>

        <p className="mt-6 text-sm text-neutral-400">Already a member? <Link to="/login" className="text-[#D4AF37] hover:underline">Log in</Link></p>
      </div>
    </div>
  );
}
